import { Link } from 'react-router-dom';
import { Icon, IconBox } from '../components/Icons.jsx';

function Home() {
  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-content">
          <p className="hero-eyebrow">Web Development & Managed Hosting</p>
          <h1 className="hero-title">Your Website Should Work as Hard as You Do</h1>
          <p className="hero-subtitle">
            Custom development, managed hosting, and proactive website care — so your business stays secure,
            fast, and ready for growth without the technical headaches.
          </p>
          <div className="hero-cta">
            <Link to="/contact" className="cta-button">Book a Free Consultation</Link>
            <Link to="/pricing" className="cta-button secondary">View Pricing</Link>
          </div>
          <div className="hero-trust-list">
            <span><Icon name="check" className="hero-trust-icon" /> Free 30-minute consultation</span>
            <span><Icon name="check" className="hero-trust-icon" /> Response within one business day</span>
            <span><Icon name="check" className="hero-trust-icon" /> No long-term contracts required</span>
          </div>
        </div>
      </section>

      <section className="trust-bar" aria-label="Service highlights">
        <div className="trust-bar-container">
          <div className="trust-stat">
            <strong>99.9%</strong>
            <span>Uptime target on managed hosting</span>
          </div>
          <div className="trust-stat">
            <strong>Daily</strong>
            <span>Backups on professional plans</span>
          </div>
          <div className="trust-stat">
            <strong>SSL</strong>
            <span>Included on every hosted site</span>
          </div>
          <div className="trust-stat">
            <strong>Hands-on</strong>
            <span>Support from real specialists</span>
          </div>
        </div>
      </section>

      <section className="services-section">
        <div className="services-container">
          <h2 className="section-title">What We Do</h2>
          <p className="section-subtitle">
            End-to-end web services for business owners who need dependable technology and a partner they can reach.
          </p>

          <div className="services-grid">
            <div className="service-card">
              <IconBox name="development" />
              <h3>Custom Web Applications</h3>
              <p>Modern apps built with React, Node.js, and cloud infrastructure tailored to your workflows.</p>
            </div>
            <div className="service-card">
              <IconBox name="design" />
              <h3>Website Development</h3>
              <p>Professional websites and CMS builds — WordPress, static sites, and custom stacks.</p>
            </div>
            <div className="service-card">
              <IconBox name="hosting" />
              <h3>Managed Hosting</h3>
              <p>Secure hosting with monitoring, backups, and performance optimization included.</p>
            </div>
            <div className="service-card">
              <IconBox name="maintenance" />
              <h3>APIs & Integrations</h3>
              <p>Custom APIs, third-party integrations, and database design to connect your systems.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">Why Businesses Choose The Hosting Hive</h2>
          <p className="section-subtitle">
            A dedicated partner focused on reliability, clear communication, and long-term results.
          </p>

          <div className="features-grid">
            <div className="feature-item">
              <IconBox name="development" />
              <div className="feature-content">
                <h4>Custom Solutions</h4>
                <p>Applications engineered for your business — not one-size-fits-all templates.</p>
              </div>
            </div>
            <div className="feature-item">
              <IconBox name="performance" />
              <div className="feature-content">
                <h4>Modern Technology</h4>
                <p>React, Node.js, and cloud infrastructure built for speed and maintainability.</p>
              </div>
            </div>
            <div className="feature-item">
              <IconBox name="growth" />
              <div className="feature-content">
                <h4>Scalable Architecture</h4>
                <p>Systems designed to grow from startup to enterprise without costly rewrites.</p>
              </div>
            </div>
            <div className="feature-item">
              <IconBox name="support" />
              <div className="feature-content">
                <h4>Partnership Approach</h4>
                <p>Ongoing support and strategy from specialists who know your stack.</p>
              </div>
            </div>
            <div className="feature-item">
              <IconBox name="security" />
              <div className="feature-content">
                <h4>Security-First</h4>
                <p>SSL, updates, monitoring, and best practices built into every project.</p>
              </div>
            </div>
            <div className="feature-item">
              <IconBox name="monitoring" />
              <div className="feature-content">
                <h4>Performance Optimized</h4>
                <p>Continuous monitoring and tuning so your applications perform at their best.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="services-section">
        <div className="services-container">
          <h2 className="section-title">Who We Work With</h2>
          <p className="section-subtitle">
            Owners and teams who need dependable web operations without building an in-house department.
          </p>
          <div className="audience-grid">
            <div className="audience-card">
              <h3>Small Businesses</h3>
              <p>Professional sites and hosting that look credible and convert visitors into customers.</p>
            </div>
            <div className="audience-card">
              <h3>Growing Companies</h3>
              <p>Scalable hosting, maintenance, and development as your needs increase.</p>
            </div>
            <div className="audience-card">
              <h3>Service Providers</h3>
              <p>Reliable operations for consultants, agencies, and local businesses that depend on their site daily.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="process-section">
        <div className="services-container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">A clear process that keeps projects moving and communication straightforward.</p>
          <div className="process-grid">
            <div className="process-step">
              <div className="step-number">1</div>
              <h4>Discovery Call</h4>
              <p>We review your goals, current setup, and priorities in a free consultation.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h4>Plan & Proposal</h4>
              <p>You receive a clear recommendation with scope, timeline, and pricing.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h4>Launch & Care</h4>
              <p>We build, deploy, and stay with you for ongoing support and optimization.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready for a Technology Partner You Can Count On?</h2>
          <p>Tell us about your project and we&apos;ll recommend the right development or hosting path.</p>
          <Link to="/contact" className="cta-button">Book Your Free Consultation</Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
